import React, { useState, useEffect } from "react";
import { Card, Row, Col, Container, Button, ProgressBar, Spinner, Form, Alert, Modal, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import CustomProgressBar from "views/Components/CustomProgressBar ";
import ReactivateNumbersModal from "views/Components/ReactivateNumbersModal";
import axios from "axios";
import { API_BASE_URL } from "config.js";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function Dashboard() {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(true);
  const [deactivatedNumbers, setDeactivatedNumbers] = useState([]);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("all");
  
  const [stats, setStats] = useState({
    totalDelivered: 0,
    totalReplies: 0,
    chartData: { labels: [], data: [] }
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // Onboarding States
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showApprovalAlert, setShowApprovalAlert] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (currentUser && currentUser.type !== "admin") {
      const isApproved = currentUser.businessRegistrationStatus === "approved" && currentUser.campaignRegistrationStatus === "approved";
      const hasDismissed = localStorage.getItem("dismissedApprovalAlert");
      if (isApproved && !hasDismissed) {
        setShowApprovalAlert(true);
      }
    }
  }, [currentUser]);
  const [isSyncing, setIsSyncing] = useState(false);

  // Form Fields
  const [businessName, setBusinessName] = useState("");
  const [ein, setEin] = useState("");
  const [website, setWebsite] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState("US");
  const [entityType, setEntityType] = useState("Corporation");
  const [campaignDescription, setCampaignDescription] = useState("");
  const [sampleMessage1, setSampleMessage1] = useState("");
  const [sampleMessage2, setSampleMessage2] = useState("");
  const [optInWorkflow, setOptInWorkflow] = useState("");
  const [onboardingError, setOnboardingError] = useState("");
  const [isSubmittingOnboarding, setIsSubmittingOnboarding] = useState(false);
  const [isQuickPurchasing, setIsQuickPurchasing] = useState(false);

  useEffect(() => {
    const checkRedirect = async () => {
      await fetchCampaigns();
      const freshUser = await fetchCredits();
      await fetchPhoneNumbers();

      const query = new URLSearchParams(window.location.search);
      if (query.get("onboardSuccess") === "true") {
        // Silently clear the query string from URL bar
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Restore draft from localStorage if present
        const draftStr = localStorage.getItem("onboardingDraft");
        if (draftStr) {
          try {
            const draft = JSON.parse(draftStr);
            setBusinessName(draft.businessName || "");
            setEin(draft.ein || "");
            setWebsite(draft.website || "");
            setStreet(draft.street || "");
            setCity(draft.city || "");
            setStateVal(draft.stateVal || "");
            setZip(draft.zip || "");
            setCountry(draft.country || "US");
            setEntityType(draft.entityType || "Corporation");
            setCampaignDescription(draft.campaignDescription || "");
            setSampleMessage1(draft.sampleMessage1 || "");
            setSampleMessage2(draft.sampleMessage2 || "");
            setOptInWorkflow(draft.optInWorkflow || "");
            
            localStorage.removeItem("onboardingDraft"); // Cleanup
          } catch (e) {
            console.error("Failed to restore onboarding draft:", e);
          }
        } else if (freshUser) {
          openOnboarding(freshUser);
        }
        
        setStep(3);
        setShowOnboarding(true);
      }
    };
    
    checkRedirect();
  }, []);

  const currentUserId = currentUser?._id || currentUser?.id;

  useEffect(() => {
    fetchStats();
  }, [selectedCampaign, currentUserId]);

  const openOnboarding = (userToUse) => {
    const userObj = userToUse || currentUser;
    if (userObj) {
      setBusinessName(userObj.businessDetails?.legalName || "");
      setEin(userObj.businessDetails?.taxId || "");
      setWebsite(userObj.businessDetails?.website || "");
      const addrParts = userObj.businessDetails?.address?.split(",") || [];
      setStreet(addrParts[0]?.trim() || "");
      setCity(addrParts[1]?.trim() || "");
      const stateZip = addrParts[2]?.trim().split(" ") || [];
      setStateVal(stateZip[0]?.trim() || "");
      setZip(stateZip[1]?.trim() || "");
      setCountry(addrParts[3]?.trim() || "US");
      setEntityType(userObj.businessDetails?.businessType || "Corporation");
      setCampaignDescription(userObj.campaignDetails?.description || "");
      setSampleMessage1(userObj.campaignDetails?.sampleMessages?.[0] || "");
      setSampleMessage2(userObj.campaignDetails?.sampleMessages?.[1] || "");
      setOptInWorkflow(userObj.campaignDetails?.optInDetails || "");
    }
    setStep(1);
    setOnboardingError("");
    setShowOnboarding(true);
  };

  const handleQuickBuy = async () => {
    // Save current onboarding form values to localStorage
    const onboardingDraft = {
      businessName,
      ein,
      website,
      street,
      city,
      stateVal,
      zip,
      country,
      entityType,
      campaignDescription,
      sampleMessage1,
      sampleMessage2,
      optInWorkflow
    };
    localStorage.setItem("onboardingDraft", JSON.stringify(onboardingDraft));

    setIsQuickPurchasing(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_BASE_URL}/billing/buy-credits`,
        { 
          amount: 25,
          successUrl: `${window.location.origin}/admin/dashboard?onboardSuccess=true`,
          cancelUrl: `${window.location.origin}/admin/dashboard`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error("Quick Purchase error:", error);
      alert(error.response?.data?.message || "Failed to initiate purchase.");
      setIsQuickPurchasing(false);
    }
  };

  const fetchCredits = async () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      const token = localStorage.getItem("token");
      if (storedUser && token) {
        const userId = storedUser.id || storedUser._id;
        const res = await axios.get(`${API_BASE_URL}/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          const userData = res.data.data;
          setCredits(userData.credits || 0);
          setCurrentUser(userData);
          // Sync user metadata back to localStorage
          localStorage.setItem("user", JSON.stringify(userData));

          // Auto-Sync trigger if vetting status is currently pending
          if (userData.businessRegistrationStatus === "pending" || userData.campaignRegistrationStatus === "pending") {
            axios.post(`${API_BASE_URL}/users/sync-a2p-status`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(syncRes => {
              if (syncRes.data.success) {
                if (
                  syncRes.data.businessRegistrationStatus !== userData.businessRegistrationStatus ||
                  syncRes.data.campaignRegistrationStatus !== userData.campaignRegistrationStatus
                ) {
                  const freshlySyncedUser = {
                    ...userData,
                    businessRegistrationStatus: syncRes.data.businessRegistrationStatus,
                    campaignRegistrationStatus: syncRes.data.campaignRegistrationStatus,
                    twilioMessagingServiceSid: syncRes.data.twilioMessagingServiceSid
                  };
                  setCurrentUser(freshlySyncedUser);
                  localStorage.setItem("user", JSON.stringify(freshlySyncedUser));
                }
              }
            }).catch(e => console.error("Auto background A2P sync failed:", e));
          }
          return userData;
        }
      }
    } catch (err) {
      console.error("Failed to fetch user credits", err);
    } finally {
      setLoadingCredits(false);
    }
  };

  const fetchPhoneNumbers = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API_BASE_URL}/phone-numbers/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        const inactive = res.data.phoneNumbers.filter(n => {
          const daysSinceLastBill = n.lastBilledAt 
            ? (Date.now() - new Date(n.lastBilledAt).getTime()) / (1000 * 60 * 60 * 24)
            : 30;
          return n.isPastDue || (!n.isActive && daysSinceLastBill >= 29.5);
        });
        setDeactivatedNumbers(inactive);
      }
    } catch (err) {
      console.error("Failed to fetch phone numbers on dashboard", err);
    }
  };

  const handleManualSync = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      setIsSyncing(true);
      const res = await axios.post(`${API_BASE_URL}/users/sync-a2p-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        await fetchCredits();
      }
    } catch (err) {
      console.error("Manual status sync failed:", err);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleOnboardingSubmit = async () => {
    setOnboardingError("");

    if (!website || !website.toLowerCase().startsWith("https://")) {
      setOnboardingError("Website URL is required and must start with https:// (e.g., https://yourwebsite.com)");
      return;
    }

    setIsSubmittingOnboarding(true);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API_BASE_URL}/users/onboard`, {
        businessDetails: {
          legalName: businessName,
          taxId: ein,
          website: website,
          address: `${street}, ${city}, ${stateVal} ${zip}, ${country}`,
          vertical: "Political",
          businessType: entityType === "Non-profit / Charity" ? "Non-Profit" : "Corporation"
        },
        campaignDetails: {
          useCase: entityType === "Non-profit / Charity" ? "charity" : "political",
          description: campaignDescription,
          sampleMessages: [sampleMessage1, sampleMessage2],
          optInDetails: optInWorkflow
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        setShowOnboarding(false);
        // Refresh credits and currentUser profile status
        fetchCredits();
      } else {
        setOnboardingError(res.data.message || "Failed to submit onboarding.");
      }
    } catch (err) {
      console.error("Onboarding submission failed:", err);
      setOnboardingError(err.response?.data?.message || "An error occurred during submission.");
    } finally {
      setIsSubmittingOnboarding(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API_BASE_URL}/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setCampaigns(res.data.campaigns);
      }
    } catch (err) {
      console.error("Failed to fetch campaigns", err);
    }
  };

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API_BASE_URL}/dashboard/stats?campaignId=${selectedCampaign}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard stats", err);
    } finally {
      setLoadingStats(false);
    }
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col className="d-flex justify-content-between align-items-center p-3">
          <h2 className="m-0 font-weight-bold">Welcome to OrcaX</h2>
          <div style={{ minWidth: "250px" }}>
            <Form.Group className="mb-0">
              <Form.Label className="font-weight-bold">Filter by Campaign:</Form.Label>
              <Form.Control
                as="select"
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
                style={{ cursor: "pointer", borderRadius: "8px", border: "1px solid #7A7A7A" }}
              >
                <option value="all">All Campaigns</option>
                {campaigns.map(camp => (
                  <option key={camp._id} value={camp._id}>{camp.name}</option>
                ))}
              </Form.Control>
            </Form.Group>
          </div>
        </Col>
      </Row>

      {/* Low Balance for Phone Rentals Alert */}
      {(() => {
        const activeNumbersCount = currentUser?.phoneNumbers?.length || 0;
        const renewalCost = activeNumbersCount * 200;
        const hasLowBalanceForRentals = activeNumbersCount > 0 && credits !== null && credits < renewalCost;
        if (!hasLowBalanceForRentals) return null;
        return (
          <Row className="mb-3">
            <Col md={12}>
              <Alert 
                variant="warning" 
                className="border-0 shadow-sm p-4 d-flex align-items-center justify-content-between flex-column flex-md-row" 
                style={{ borderRadius: "10px", backgroundColor: "#fff3cd", borderLeft: "5px solid #ffc107" }}
              >
                <div className="text-start">
                  <h4 className="alert-heading font-weight-bold mt-0 mb-1" style={{ color: "#856404", fontWeight: "700" }}>
                    <i className="fas fa-exclamation-triangle me-2"></i> Low Balance Alert: Phone Number Renewals
                  </h4>
                  <p className="mb-0 text-dark font-weight-bold">
                    You currently have <strong>{activeNumbersCount}</strong> active phone number{activeNumbersCount !== 1 ? "s" : ""} on your account. The upcoming monthly renewal cost is <strong>${(renewalCost / 100).toFixed(2)} ({renewalCost} credits)</strong>, but your balance is only <strong>${(credits / 100).toFixed(2)}</strong>. Please top up your balance to prevent your phone numbers from being released.
                  </p>
                </div>
                <Button 
                  onClick={handleQuickBuy}
                  className="mt-3 mt-md-0 fw-bold"
                  style={{ backgroundColor: "#ffc107", border: "none", color: "#000", fontWeight: "700", marginLeft: "15px", borderRadius: "8px", padding: "10px 25px" }}
                >
                  Buy Credits
                </Button>
              </Alert>
            </Col>
          </Row>
        );
      })()}

      {/* Deactivated Numbers Alert */}
      {deactivatedNumbers.length > 0 && (
        <Row className="mb-3">
          <Col md={12}>
            <Alert 
              variant="danger" 
              className="border-0 shadow-sm p-4 d-flex align-items-center justify-content-between flex-column flex-md-row" 
              style={{ borderRadius: "10px", backgroundColor: "#f8d7da", borderLeft: "5px solid #dc3545" }}
            >
              <div className="text-start">
                <h4 className="alert-heading font-weight-bold mt-0 mb-1" style={{ color: "#721c24", fontWeight: "700" }}>
                  <i className="fas fa-exclamation-circle me-2"></i> Action Required: Deactivated Phone Numbers
                </h4>
                <p className="mb-0 text-dark font-weight-bold">
                  You have <strong>{deactivatedNumbers.length}</strong> phone number(s) deactivated due to insufficient credits. Please reactivate them to resume routing.
                </p>
              </div>
              <div className="d-flex mt-3 mt-md-0" style={{ gap: "10px" }}>
                <Button 
                  onClick={() => setShowReactivateModal(true)}
                  className="fw-bold btn-primary"
                  style={{ fontWeight: "700", borderRadius: "8px", padding: "10px 25px" }}
                >
                  Reactivate Numbers
                </Button>
                <Button 
                  onClick={() => navigate("/admin/billing")}
                  className="fw-bold bg-danger border-0 text-white"
                  style={{ fontWeight: "700", borderRadius: "8px", padding: "10px 25px" }}
                >
                  Buy Credits
                </Button>
              </div>
            </Alert>
          </Col>
        </Row>
      )}


      <Row className="mt-3">
        {/* SMS Credits Box */}
        <Col lg={4} className="d-flex align-items-stretch">
          <Card className="w-100" style={{ border: "1.5px solid #7A7A7A", borderRadius: "10px" }}>
            <Card.Body style={{ padding: "25px 30px" }} className="h-100 d-flex flex-column justify-content-around">
              <div className="d-flex justify-content-between">
                <h5 className="mb-0" style={{ fontWeight: "700" }}>
                  Available SMS Credits
                </h5>
                <i
                  className="fas fa-coins text-warning"
                  style={{ fontSize: "1.3rem", marginLeft: "8px" }}
                ></i>
              </div>
              <h1 style={{ fontWeight: "800", fontSize: "3rem", color: "#014c95" }} className="my-2">
                {loadingCredits ? <Spinner animation="border" size="sm" /> : credits !== null ? credits.toLocaleString() : "0"}
              </h1>
              <small className="mb-4 text-muted" style={{ fontWeight: "700" }}>
                Remaining credits for campaigns
              </small>
              <Button style={{ backgroundColor: "black", borderRadius: '8px', border: "none" }} className="fw-bold w-100 py-2" onClick={() => navigate("/admin/billing")}>
                Buy Credits
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {/* Total Delivered Box */}
        <Col lg={4} className="d-flex align-items-stretch">
          <Card className="w-100" style={{ border: "1.5px solid #7A7A7A", borderRadius: "10px" }}>
            <Card.Body style={{ padding: "25px 30px" }} className="h-100 d-flex flex-column justify-content-around">
              <div className="d-flex justify-content-between">
                <h5 className="mb-0" style={{ fontWeight: "700" }}>
                  Total Delivered
                </h5>
                <i
                  className="far fa-paper-plane ms-2 text-dark"
                  style={{ fontSize: "1.3rem", marginLeft: "8px" }}
                ></i>
              </div>
              <h2 style={{ fontWeight: "700" }} className="my-2">
                {loadingStats ? <Spinner animation="border" size="sm" /> : stats.totalDelivered.toLocaleString()}
              </h2>
              <small className="mb-0 text-muted" style={{ fontWeight: "700" }}>
                {selectedCampaign === "all" ? "Across all campaigns" : "For selected campaign"}
              </small>
            </Card.Body>
          </Card>
        </Col>

        {/* Total Replies Box */}
        <Col lg={4} className="d-flex align-items-stretch">
          <Card className="w-100" style={{ border: "1.5px solid #7A7A7A", borderRadius: "10px" }}>
            <Card.Body style={{ padding: "25px 30px" }} className="h-100 d-flex flex-column justify-content-around">
              <div className="d-flex justify-content-between">
                <h5 className="mb-0" style={{ fontWeight: "700" }}>
                  Total Replies
                </h5>
                <i
                  className="fas fa-reply ms-2 text-dark"
                  style={{ fontSize: "1.3rem", marginLeft: "8px" }}
                ></i>
              </div>
              <h2 style={{ fontWeight: "700" }} className="my-2">
                {loadingStats ? <Spinner animation="border" size="sm" /> : stats.totalReplies.toLocaleString()}
              </h2>
              <small className="mb-0 text-muted" style={{ fontWeight: "700" }}>
                {selectedCampaign === "all" ? "Total incoming responses" : "From contacts in this campaign"}
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Analytics Chart Row */}
      <Row className="mt-2 mb-5">
        <Col lg={12}>
          <Card className="w-100" style={{ border: "1.5px solid #7A7A7A", borderRadius: "10px" }}>
            <Card.Header className="pt-4 pb-2 px-4 border-0 bg-white" style={{ borderTopLeftRadius: "10px", borderTopRightRadius: "10px" }}>
              <h4 className="card-title fw-bold m-0" style={{ fontWeight: "800", color: "#333" }}>Campaign Performance</h4>
              <p className="card-category m-0 fw-bold" style={{ fontWeight: "700", color: "#888" }}>Messages delivered over the last 7 days</p>
            </Card.Header>
            <Card.Body className="px-4 pb-4 pt-0">
               <div style={{ height: "300px" }}>
                 {loadingStats ? (
                   <div className="d-flex justify-content-center align-items-center h-100">
                     <Spinner animation="border" />
                   </div>
                 ) : (
                   <Line
                      data={{
                        labels: stats.chartData.labels,
                        datasets: [
                          {
                            label: "Messages Delivered",
                            data: stats.chartData.data,
                            borderColor: "#014c95",
                            backgroundColor: "rgba(1, 76, 149, 0.1)",
                            borderWidth: 3,
                            pointBackgroundColor: "#014c95",
                            pointRadius: 4,
                            fill: true,
                            tension: 0.4
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: "top", labels: { font: { weight: "bold", family: "Inter" } } },
                          tooltip: { mode: "index", intersect: false }
                        },
                        scales: {
                          x: { grid: { display: false }, ticks: { font: { weight: "bold" } } },
                          y: { 
                            grid: { borderDash: [5, 5] }, 
                            ticks: { font: { weight: "bold" }, stepSize: 1, precision: 0 },
                            beginAtZero: true
                          }
                        }
                      }}
                   />
                 )}
               </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Onboarding Wizard Modal */}
      <Modal show={showOnboarding} onHide={() => setShowOnboarding(false)} size="lg" backdrop="static" keyboard={false} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ fontWeight: "800", fontSize: "1.5rem" }}>
            A2P 10DLC Brand & Campaign Registration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <div className="mb-4">
            <ProgressBar now={(step / 3) * 100} variant="primary" style={{ height: "8px", borderRadius: "4px" }} />
            <div className="d-flex justify-content-between mt-2 font-weight-bold text-muted small">
              <span className={step === 1 ? "text-primary fw-bold" : ""}>1. Brand Profile</span>
              <span className={step === 2 ? "text-primary fw-bold" : ""}>2. Campaign Details</span>
              <span className={step === 3 ? "text-primary fw-bold" : ""}>3. Review & Payment</span>
            </div>
          </div>

          {onboardingError && (
            <Alert variant="danger" className="py-2">
              {onboardingError}
            </Alert>
          )}

          {step === 1 && (
            <Form onSubmit={(e) => { e.preventDefault(); setStep(2); }}>
              <h5 className="font-weight-bold text-dark mb-3">Step 1: Brand & Entity Profile</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="font-weight-bold text-dark">Business Legal Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="As registered with IRS"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="font-weight-bold text-dark">Entity Type</Form.Label>
                    <Form.Control
                      as="select"
                      value={entityType}
                      onChange={(e) => setEntityType(e.target.value)}
                      required
                    >
                      <option value="Corporation">Corporation</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Sole Proprietorship">Sole Proprietorship</option>
                      <option value="Non-profit / Charity">Non-profit / Charity ($3/mo tier)</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="font-weight-bold text-dark">Employer Identification Number (EIN)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="9-digit IRS Tax ID (e.g. 12-3456789)"
                      value={ein}
                      onChange={(e) => setEin(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="font-weight-bold text-dark">Website URL</Form.Label>
                    <Form.Control
                      type="url"
                      placeholder="https://yourwebsite.com"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <h6 className="font-weight-bold text-dark mt-2 mb-2">Business Address</h6>
              <Form.Group className="mb-3">
                <Form.Label className="small font-weight-bold text-dark">Street Address</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="123 Main St"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  required
                />
              </Form.Group>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small font-weight-bold text-dark">City</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="City"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small font-weight-bold text-dark">State</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. TX"
                      value={stateVal}
                      onChange={(e) => setStateVal(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small font-weight-bold text-dark">ZIP Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="ZIP"
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="small font-weight-bold text-dark">Country</Form.Label>
                <Form.Control
                  as="select"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  required
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                </Form.Control>
              </Form.Group>

              <div className="d-flex justify-content-end mt-4">
                <Button type="submit" className="px-4 text-white border-0 font-weight-bold" style={{ backgroundColor: "#014c95" }}>
                  Next Step
                </Button>
              </div>
            </Form>
          )}

          {step === 2 && (
            <Form onSubmit={(e) => { e.preventDefault(); setStep(3); }}>
              <h5 className="font-weight-bold text-dark mb-3">Step 2: Campaign & Opt-in Workflow</h5>
              
              <Form.Group className="mb-3">
                <Form.Label className="font-weight-bold text-dark">Campaign Use Case Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="e.g. This campaign is used to send news updates, event notifications, and calls to action to political supporters who have subscribed."
                  value={campaignDescription}
                  onChange={(e) => setCampaignDescription(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="font-weight-bold text-dark">Sample Message 1</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="e.g. Hi [FirstName], join us for our rally this Friday at Central Park. Reply STOP to opt out."
                  value={sampleMessage1}
                  onChange={(e) => setSampleMessage1(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="font-weight-bold text-dark">Sample Message 2</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="e.g. Don't forget to submit your ballot by next Tuesday. Find your polling location here. Reply STOP to unsubscribe."
                  value={sampleMessage2}
                  onChange={(e) => setSampleMessage2(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="font-weight-bold text-dark">Opt-in / Consent Flow</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="e.g. Supporters subscribe by entering their mobile phone number on our website form (https://example.com/join) and checking a box consenting to receive SMS."
                  value={optInWorkflow}
                  onChange={(e) => setOptInWorkflow(e.target.value)}
                  required
                />
              </Form.Group>

              <div className="d-flex justify-content-between mt-4">
                <Button type="button" variant="secondary" onClick={() => setStep(1)} className="px-4 font-weight-bold">
                  Back
                </Button>
                <Button type="submit" className="px-4 text-white border-0 font-weight-bold" style={{ backgroundColor: "#014c95" }}>
                  Next Step
                </Button>
              </div>
            </Form>
          )}

          {step === 3 && (
            <div>
              <h5 className="font-weight-bold text-dark mb-3">Step 3: Summary & Carrier Fee Review</h5>
              
              <Table bordered size="sm" className="mb-4">
                <tbody>
                  <tr>
                    <td className="font-weight-bold text-muted w-25">Business Name</td>
                    <td className="text-dark font-weight-bold">{businessName}</td>
                  </tr>
                  <tr>
                    <td className="font-weight-bold text-muted">EIN</td>
                    <td className="text-dark font-weight-bold">{ein}</td>
                  </tr>
                  <tr>
                    <td className="font-weight-bold text-muted">Entity Type</td>
                    <td className="text-dark font-weight-bold">{entityType}</td>
                  </tr>
                  <tr>
                    <td className="font-weight-bold text-muted">Business Address</td>
                    <td className="text-dark font-weight-bold">{street}, {city}, {stateVal} {zip}, {country}</td>
                  </tr>
                </tbody>
              </Table>

              <div className="p-3 bg-light rounded border mb-4">
                <h6 className="font-weight-bold text-dark mb-2">Carrier & Registration Vetting Fees</h6>
                <div className="d-flex justify-content-between py-1 border-bottom">
                  <span className="text-muted">Brand & Campaign Vetting (One-time carrier charge)</span>
                  <span className="font-weight-bold text-dark">$19.41 (1,941 credits)</span>
                </div>
                <div className="d-flex justify-content-between py-1 border-bottom">
                  <span className="text-muted">Carrier Campaign Fee (Monthly recurring charge)</span>
                  <span className="font-weight-bold text-dark">
                    {entityType === "Non-profit / Charity" ? "$3.00 / mo (300 credits)" : "$10.00 / mo (1,000 credits)"}
                  </span>
                </div>
                <div className="d-flex justify-content-between py-2 mt-2">
                  <span className="font-weight-bold text-primary">Your Available Balance</span>
                  <span className="font-weight-bold text-primary">${credits !== null ? (credits / 100).toFixed(2) : "0.00"}</span>
                </div>
              </div>

              {credits < 1941 && (
                <Alert variant="danger" className="mb-4 border-0 p-4 shadow-sm" style={{ borderRadius: "10px", backgroundColor: "#f8d7da" }}>
                  <div className="d-flex align-items-center justify-content-between flex-column flex-md-row">
                    <div className="mb-3 mb-md-0 font-weight-bold text-start" style={{ color: "#721c24" }}>
                      <i className="fas fa-exclamation-circle me-2"></i>
                      <strong>Insufficient Credits:</strong> You need at least $19.41 (1,941 credits) to register. Your balance is currently ${(credits / 100).toFixed(2)}.
                    </div>
                    <Button
                      variant="danger"
                      className="font-weight-bold px-4 py-2 text-white border-0 ms-md-3"
                      style={{ borderRadius: "8px", minWidth: "220px", backgroundColor: "#dc3545" }}
                      disabled={isQuickPurchasing}
                      onClick={handleQuickBuy}
                    >
                      {isQuickPurchasing ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" /> Redirecting...
                        </>
                      ) : (
                        "Buy 2,500 Credits ($25.00)"
                      )}
                    </Button>
                  </div>
                </Alert>
              )}

              <div className="d-flex justify-content-between mt-4">
                <Button type="button" variant="secondary" onClick={() => setStep(2)} className="px-4 font-weight-bold" disabled={isSubmittingOnboarding}>
                  Back
                </Button>
                <Button
                  type="button"
                  className="px-4 text-white border-0 font-weight-bold"
                  style={{ backgroundColor: "#014c95" }}
                  onClick={handleOnboardingSubmit}
                  disabled={credits < 1941 || isSubmittingOnboarding}
                >
                  {isSubmittingOnboarding ? "Submitting Registration..." : `Pay $19.41 & Register`}
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <ReactivateNumbersModal 
        show={showReactivateModal}
        onHide={() => setShowReactivateModal(false)}
        deactivatedNumbers={deactivatedNumbers}
        userCredits={credits || 0}
        onSuccess={(updatedCredits) => {
          setCredits(updatedCredits);
          fetchPhoneNumbers();
        }}
      />

    </Container >
  );
}

export default Dashboard;
